import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
// eslint-disable-next-line no-restricted-imports
import { Link, useParams } from 'react-router';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { Centered } from '#src/atoms/layout/Centered/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import {
  LinkBox,
  LinkOther,
  LinkOverlay,
} from '#src/atoms/navigation/Box/index.tsx';
import { BodyS, BrandM, BrandS } from '#src/atoms/typography/index.tsx';
import { Img } from '#src/atoms/ui/Img/index.tsx';
import { CREDIT_TERMS } from '#src/constants/__generated__/routes.ts';
import { useConnectionContext } from '#src/contexts/connection/index.tsx';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useIsLaptop } from '#src/hooks/device/useIsLaptop.ts';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';
import { useAmountWithConversion } from '#src/hooks/useAmountWithConversion.ts';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { useLocalStorage } from '#src/hooks/useLocalStorage.ts';
import { useEvents } from '#src/lib/events/useEvents.ts';
import { monetaryAmountFragment } from '#src/lib/monetaryAmount.ts';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';
import { borderGradientStyle } from '#src/style/utils.tsx';

import type {
  LandingPromoBannerQuery,
  LandingPromoBannerQueryVariables,
} from './__generated__/index.graphql.ts';
import icon from './assets/Icon.webp';
import bg from './assets/bg.webp';
import mobileBg from './assets/mobileBg.webp';

const LinkBoxWrapper = styled(LinkBox)`
  --border-gradient: var(--c-gradient-discount);
  --border-size: 2px;
  ${borderGradientStyle};
  background-image: url(${mobileBg});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 20px;
  padding: var(--unit) var(--double-unit);
  @media ${laptopAndAbove} {
    background-image: url(${bg});
  }
`;

const Content = styled.div`
  justify-content: flex-start;
  width: 100%;
`;

const Description = styled(BrandM)`
  @media ${laptopAndAbove} {
    font: var(--t-brand-m);
  }
`;

const UnderlinedText = styled.span`
  text-decoration: underline;
  &:hover {
    color: var(--c-nd-700);
  }
`;

const LinkOverlayMobileContainer = styled(LinkOverlay)`
  display: flex;
  flex-direction: column;
  gap: var(--unit);
  text-align: left;
`;

const PromoTitle = () => {
  return (
    <Horizontal gap={1} center>
      <Img src={icon} height="30" width="30" />
      <BrandS>
        <FormattedMessage defaultMessage="Welcome Offer" />
      </BrandS>
    </Horizontal>
  );
};

const PromoDescription = ({
  discountAmount,
}: {
  discountAmount: ReactNode;
}) => {
  return (
    <Description className="text-center">
      <FormattedMessage
        defaultMessage="Get {amount} free on your first cards!*"
        values={{
          amount: discountAmount,
        }}
      />
    </Description>
  );
};

const PromoDetails = ({
  discountAmount,
  discountPercentage,
  termsAndConditionsUrl,
}: {
  discountPercentage: number;
  discountAmount: ReactNode;
  termsAndConditionsUrl: string;
}) => {
  return (
    <BodyS>
      <FormattedMessage
        defaultMessage="*{percent} offered in credits, up to {amount} off. {link}"
        values={{
          amount: discountAmount,
          percent: (
            <FormattedNumber value={discountPercentage} style="percent" />
          ),
          link: (
            <LinkOther as={Link} to={termsAndConditionsUrl}>
              <UnderlinedText>
                <FormattedMessage defaultMessage="Terms apply" />
              </UnderlinedText>
            </LinkOther>
          ),
        }}
      />
    </BodyS>
  );
};

const PromoButton = () => {
  const { signUp } = useConnectionContext();
  const track = useEvents();
  const leagueParam = useParams();
  const league = Object.values(leagueParam)[0];
  const onClick = () => {
    track('Click Landing Page Promo Banner', {
      clickSource: league,
    });
    signUp();
  };
  return (
    <LinkOverlay as={ButtonBase} onClick={onClick}>
      <Button asSpan color="secondary" size="small">
        <FormattedMessage defaultMessage="Claim now!" />
      </Button>
    </LinkOverlay>
  );
};

type Props = {
  league?: string;
};

const LANDING_PROMO_BANNER_QUERY = gql`
  query LandingPromoBannerQuery($sport: Sport!, $affiliate: Boolean!) {
    market {
      id
      onboardingConversionCredit(sport: $sport, affiliate: $affiliate) {
        id
        percentageDiscount
        maxDiscount {
          ...MonetaryAmountFragment_monetaryAmount
        }
      }
    }
  }
  ${monetaryAmountFragment}
` as TypedDocumentNode<
  LandingPromoBannerQuery,
  LandingPromoBannerQueryVariables
>;

const LandingPromoBannerComponent = ({
  onboardingConversionCredit,
  league,
}: {
  onboardingConversionCredit: NonNullable<
    LandingPromoBannerQuery['market']['onboardingConversionCredit']
  >;
  league: string | undefined;
}) => {
  const isLaptop = useIsLaptop();
  const { signUp } = useConnectionContext();
  const track = useEvents();

  const {
    flags: { useLandingPagePromoBanner = false },
  } = useFeatureFlags();
  const { main: discountAmount } = useAmountWithConversion({
    monetaryAmount: onboardingConversionCredit.maxDiscount,
    hideFractionDigits: true,
  });

  const onClick = () => {
    track('Click Landing Page Promo Banner', {
      clickSource: league,
    });
    signUp();
  };

  if (!useLandingPagePromoBanner) {
    return null;
  }
  return (
    <LinkBoxWrapper className="dark-mode">
      <Content>
        {isLaptop ? (
          <Centered left={<PromoTitle />} right={<PromoButton />}>
            <Vertical gap={0} center>
              <PromoDescription discountAmount={discountAmount} />
              <PromoDetails
                discountAmount={discountAmount}
                discountPercentage={
                  onboardingConversionCredit.percentageDiscount
                }
                termsAndConditionsUrl={CREDIT_TERMS}
              />
            </Vertical>
          </Centered>
        ) : (
          <LinkOverlayMobileContainer as={ButtonBase} onClick={onClick}>
            <Horizontal gap={0}>
              <Img src={icon} height="45" width="45" />
              <PromoDescription discountAmount={discountAmount} />
            </Horizontal>

            <PromoDetails
              discountAmount={discountAmount}
              discountPercentage={onboardingConversionCredit.percentageDiscount}
              termsAndConditionsUrl={CREDIT_TERMS}
            />
          </LinkOverlayMobileContainer>
        )}
      </Content>
    </LinkBoxWrapper>
  );
};

export const LandingPromoBanner = ({ league }: Props) => {
  const { sport } = useSportContext();

  const [utmCampaign] = useLocalStorage('UTM_CAMPAIGN', undefined);
  const { data } = useQuery(LANDING_PROMO_BANNER_QUERY, {
    variables: {
      sport,
      affiliate: !!utmCampaign,
    },
  });
  const onboardingConversionCredit = data?.market.onboardingConversionCredit;

  if (!onboardingConversionCredit) {
    return null;
  }
  return (
    <LandingPromoBannerComponent
      onboardingConversionCredit={onboardingConversionCredit}
      league={league}
    />
  );
};
